import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import projectsData from "@/data/projects.json";
import type {
  Language,
  Localized,
  Project,
  ProjectData,
} from "@/types/projects";

const data = projectsData as ProjectData[];

function isLocalized<T>(value: Localized<T>): value is Record<Language, T> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "pt" in value
  );
}

export function localize<T>(value: Localized<T>, language: string): T {
  if (!isLocalized(value)) return value;
  return value[language as Language] ?? value.pt;
}

function resolve(project: ProjectData, language: string): Project {
  const pick = <T,>(value: Localized<T>) => localize(value, language);

  return {
    ...project,
    title: pick(project.title),
    category: pick(project.category),
    description: pick(project.description),
    role: project.role && pick(project.role),
    stack: project.stack?.map(pick),
    outcome: project.outcome && pick(project.outcome),
    steps: project.steps?.map((step) => ({
      ...step,
      title: pick(step.title),
      description: pick(step.description),
    })),
  };
}

export function useProjects(): Project[] {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;

  return useMemo(
    () => data.map((project) => resolve(project, language)),
    [language],
  );
}
